import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { environment } from '../environments/environment';
import { EscalationStatus, IEscalation } from '@shs-digi-care-monorepo/interfaces';
import { EscalationPaginatedResponse, EscalationViewPaginatedResponse, EscalationView } from './escalation.model';

/**
 * Service for managing escalations via API.
 */
@Injectable({
    providedIn: 'root'
})
export class EscalationService {
    private readonly httpClient = inject(HttpClient);
    private readonly baseUrl = `${environment.apiUrl}/me/escalations`;

    /**
     * Loads escalations for the current user.
     * @param page Page number (0-indexed)
     * @param pageSize Number of items per page
     * @returns Observable of escalations
     */
    public getEscalations(page = 0, pageSize = 50): Observable<IEscalation[]> {
        const params = new HttpParams().set('page', page).set('pageSize', pageSize);
        return this.httpClient
            .get<EscalationPaginatedResponse>(this.baseUrl, { params })
            .pipe(map((response) => response.items));
    }

    /**
     * Loads escalations with full pagination metadata.
     * @param page Page number (0-indexed)
     * @param pageSize Number of items per page
     * @returns Observable of the full paginated response
     */
    public getEscalationsPaginated(page = 0, pageSize = 50): Observable<EscalationPaginatedResponse> {
        const params = new HttpParams().set('page', page).set('pageSize', pageSize);
        return this.httpClient.get<EscalationPaginatedResponse>(this.baseUrl, { params });
    }

    /**
     * Loads escalations enriched with the request displayId.
     * Fetches each request by its resourceId to resolve the displayId.
     * @param page Page number (0-indexed)
     * @param pageSize Number of items per page
     * @returns Observable of enriched escalation views with pagination metadata
     */
    public getEscalationViewsPaginated(page = 0, pageSize = 50): Observable<EscalationViewPaginatedResponse> {
        return this.getEscalationsPaginated(page, pageSize).pipe(
            switchMap((response) => {
                if (response.items.length === 0) {
                    return of({ items: [] as EscalationView[], metadata: response.metadata });
                }

                // TODO: Improve this, backend should return displayId directly
                const enriched$ = response.items.map((escalation) =>
                    this.httpClient
                        .get<{ displayId: string }>(`${environment.apiUrl}/requests/${escalation.resourceId}`)
                        .pipe(
                            map((request) => ({ ...escalation, displayId: request.displayId })),
                            catchError(() => of({ ...escalation, displayId: undefined }))
                        )
                );

                return forkJoin(enriched$).pipe(map((items) => ({ items, metadata: response.metadata })));
            })
        );
    }

    /**
     * Snoozes an escalation by its ID.
     * @param id The escalation ID
     * @returns Observable of the snoozed escalation
     */
    public snoozeEscalation(id: string): Observable<IEscalation> {
        return this.httpClient.patch<IEscalation>(`${this.baseUrl}/${id}/snooze`, {});
    }

    /**
     * Checks whether any escalations are actionable (pending or snoozed).
     */
    public hasActionableEscalations(escalations: IEscalation[]): boolean {
        return escalations.some((e) => e.status === EscalationStatus.PENDING || e.status === EscalationStatus.SNOOZED);
    }
}
